import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Form,
  AutoComplete,
  Input,
  Select,
  Checkbox,
  Button,
  Card,
  Typography,
  Divider,
  Row,
  Col,
  Space,
} from 'antd'
import { RocketOutlined, ClockCircleOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { RESEARCH_DEPTH_OPTIONS } from '../../utils/constants'
import PricingCalculator from '../Pricing/PricingCalculator'
import stockTickerService from '../../services/stockTickerService'
import './TaskForm.css'

const { Title, Text } = Typography
const { Option } = Select

const TaskForm = ({
  onSubmit,
  loading,
  billingSummary,
  billingLoading,
  quote,
  quoteLoading,
  onFormChange,
  prefill = {},
}) => {
  const [form] = Form.useForm()
  const navigate = useNavigate()
  const [tickerOptions, setTickerOptions] = useState([])
  const allTickersRef = useRef([])

  // Load all tickers once on mount for client-side filtering
  useEffect(() => {
    stockTickerService.getAll().then((data) => {
      allTickersRef.current = data
    }).catch(() => {})
  }, [])

  const handleTickerSearch = useCallback((value) => {
    const results = stockTickerService.filter(allTickersRef.current, value)
    setTickerOptions(
      results.map((t) => ({
        value: t.symbol,
        label: (
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ fontWeight: 600 }}>{t.symbol}</span>
            <span style={{ color: '#8c8c8c', fontSize: 12, marginLeft: 8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 200 }}>
              {t.name}
            </span>
          </div>
        ),
      }))
    )
  }, [])

  const analystOptions = [
    { label: 'Market Analyst', value: 'market' },
    { label: 'Social Analyst', value: 'social' },
    { label: 'News Analyst', value: 'news' },
    { label: 'Fundamentals Analyst', value: 'fundamentals' },
  ]

  const handleSubmit = (values) => {
    const formattedValues = {
      ticker: values.ticker.toUpperCase(),
      analysisDate: dayjs().format('YYYY-MM-DD'), // Automatically use today's date
      selectedAnalysts: values.selectedAnalysts,
      researchDepth: values.researchDepth,
    }
    onSubmit(formattedValues)
  }

  useEffect(() => {
    const initialValues = form.getFieldsValue(true)
    onFormChange?.({
      researchDepth: initialValues.researchDepth,
      selectedAnalysts: initialValues.selectedAnalysts,
    })
  }, [form, onFormChange])

  useEffect(() => {
    if (!prefill || Object.keys(prefill).length === 0) {
      return
    }
    const nextValues = {}
    if (prefill.ticker) {
      nextValues.ticker = prefill.ticker.toUpperCase()
    }
    if (prefill.researchDepth) {
      nextValues.researchDepth = prefill.researchDepth
    }
    if (prefill.selectedAnalysts && prefill.selectedAnalysts.length > 0) {
      nextValues.selectedAnalysts = prefill.selectedAnalysts
    }
    if (Object.keys(nextValues).length > 0) {
      form.setFieldsValue(nextValues)
      const updatedValues = form.getFieldsValue(true)
      onFormChange?.({
        researchDepth: updatedValues.researchDepth,
        selectedAnalysts: updatedValues.selectedAnalysts,
      })
    }
  }, [prefill, form, onFormChange])

  const handleValuesChange = (_, allValues) => {
    onFormChange?.({
      researchDepth: allValues.researchDepth,
      selectedAnalysts: allValues.selectedAnalysts,
    })
  }


  return (
    <div className="task-form-wrapper">
      <Card className="task-form" variant="borderless">
        <div className="task-form__hero">
          <div className="task-form__hero-icon">
            <RocketOutlined />
          </div>
          <div className="task-form__hero-content">
            <Title level={2} className="task-form__hero-title">
              New Market Analysis
            </Title>
            <Text className="task-form__hero-subtitle">
              Configure your analysis parameters. Our AI agents will handle the research and provide actionable insights.
            </Text>
          </div>
        </div>

        <div className="task-form__body">
          <Divider className="task-form__divider" />

          <Form
            form={form}
            layout="vertical"
            className="task-form__form"
            onFinish={handleSubmit}
            onValuesChange={handleValuesChange}
            initialValues={{
              researchDepth: 1,
              selectedAnalysts: ['market'],
            }}
            autoComplete="off"
          >
            <Form.Item
              label="Ticker Symbol"
              name="ticker"
              rules={[
                { required: true, message: 'Please input ticker symbol.' },
                {
                  pattern: /^[A-Za-z.]{1,10}$/,
                  message: 'Ticker must be 1-10 letters.',
                },
              ]}
            >
              <AutoComplete
                options={tickerOptions}
                onSearch={handleTickerSearch}
                placeholder="e.g., NVDA, AAPL, TSLA"
                size="large"
                style={{ textTransform: 'uppercase' }}
              />
            </Form.Item>

            <Form.Item
              label={(
                <div className="task-form__label">
                  <span>Research Depth</span>
                  <span className="task-form__label-hint">Higher depth provides more thorough analysis.</span>
                </div>
              )}
              name="researchDepth"
              rules={[{ required: true, message: 'Please select research depth.' }]}
            >
              <Select size="large" placeholder="Select research depth">
                {RESEARCH_DEPTH_OPTIONS.map((option) => (
                  <Option key={option.value} value={option.value}>
                    {option.label} — {option.description}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label={(
                <div className="task-form__label">
                  <span>Analysts</span>
                  <span className="task-form__label-hint">Select which specialists should contribute to the analysis.</span>
                </div>
              )}
              name="selectedAnalysts"
              rules={[
                {
                  required: true,
                  message: 'Please select at least one analyst.',
                },
              ]}
            >
              <Checkbox.Group options={analystOptions} className="task-form__checkbox-group" />
            </Form.Item>

            {/* Pricing Summary - Integrated */}
            <div style={{ marginTop: 18, marginBottom: 0 }}>
              <PricingCalculator
                quote={quote}
                quoteLoading={quoteLoading}
                billingSummary={billingSummary}
                billingLoading={billingLoading}
              />
            </div>

            <Form.Item className="task-form__cta">
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                size="large"
                block
                icon={<RocketOutlined />}
                className="task-form__cta-button"
              >
                {loading ? 'Starting analysis...' : (() => {
                  // If quote is available and not free, show "Create Analysis & Pay" with amount
                  if (quote && !quote.eligibleForFreeQuota) {
                    const numericAmount = Number(quote.totalAmount ?? 0)
                    const amountDisplay = Number.isFinite(numericAmount)
                      ? numericAmount.toFixed(2)
                      : quote.totalAmount
                    const currency = quote.currency || billingSummary?.currency || 'USD'
                    return `Create Analysis & Pay ${amountDisplay} ${currency}`
                  }
                  // Otherwise, just "Start Analysis"
                  return 'Start Analysis'
                })()}
              </Button>

              {/* Secure Payment Note */}
              {quote && !quote.eligibleForFreeQuota && (
                <div style={{ textAlign: 'center', marginTop: '12px' }}>
                  <Text style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.5 }}>
                    💳 Secure payment via Stripe
                  </Text>
                </div>
              )}

              {/* Subscription Link */}
              <div className="task-form__footer-link">
                <Button
                  type="link"
                  icon={<ClockCircleOutlined />}
                  onClick={() => {
                    const values = form.getFieldsValue()
                    const params = new URLSearchParams()
                    if (values.ticker) params.set('ticker', values.ticker.toUpperCase())
                    if (values.researchDepth) params.set('researchDepth', values.researchDepth)
                    if (values.selectedAnalysts && values.selectedAnalysts.length > 0) {
                      values.selectedAnalysts.forEach(a => params.append('analysts', a))
                    }
                    navigate(`/scheduled-tasks/new?${params.toString()}`)
                  }}
                  style={{ padding: 0, height: 'auto', fontSize: 12, fontWeight: 500 }}
                >
                  Set up a subscription for this analysis
                </Button>
              </div>
            </Form.Item>
          </Form>
        </div>
      </Card>
    </div>
  )
}

export default TaskForm
